'use client';

import clsx from 'clsx';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGSAP } from '@gsap/react';
import initAnimation from './animation/terra1-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

export default function Terra1() {
    const locale = useLocale();
    const { t } = useTranslation('terra1');

    const bgCanvasRef = useRef<HTMLCanvasElement>(null);
    const rootRef = useRef<HTMLElement>(null);
    const readyFrameIdsRef = useRef<number[]>([]);
    const [isCanvasReady, setIsCanvasReady] = useState(false);

    useGSAP(
        () => {
            setIsCanvasReady(false);
            readyFrameIdsRef.current.forEach((frameId) => {
                cancelAnimationFrame(frameId);
            });
            readyFrameIdsRef.current = [];

            const cleanup = initAnimation(
                rootRef.current!,
                bgCanvasRef.current!,
                {
                    onReady: () => {
                        if (readyFrameIdsRef.current.length > 0) {
                            return;
                        }

                        const firstFrameId = requestAnimationFrame(() => {
                            const secondFrameId = requestAnimationFrame(() => {
                                readyFrameIdsRef.current = [];
                                setIsCanvasReady(true);
                            });
                            readyFrameIdsRef.current = [
                                firstFrameId,
                                secondFrameId,
                            ];
                        });

                        readyFrameIdsRef.current = [firstFrameId];
                    },
                },
            );

            return () => {
                readyFrameIdsRef.current.forEach((frameId) => {
                    cancelAnimationFrame(frameId);
                });
                readyFrameIdsRef.current = [];
                cleanup?.();
            };
        },
        { scope: rootRef },
    );

    return (
        <section
            ref={rootRef}
            className="relative w-full justify-center overflow-hidden"
        >
            <div className="tablet:h-[484px] tablet:w-[1440px] tablet:translate-x-[-220px] absolute left-1/2 z-10 h-[400px] w-[745px] translate-x-[-160px] transform">
                <Image
                    alt="terra1 background cover"
                    fill
                    className="h-full w-full"
                    style={{
                        objectFit: 'cover',
                        objectPosition: 'center top',
                    }}
                    src={getStaticAssetUrl('/terra1/bg-terra1.webp')}
                />
            </div>

            <div className="z-10 flex w-full flex-col">
                <div className="relative">
                    <div
                        className={clsx(
                            'absolute inset-0 bg-black px-[38px] pt-[240px]',
                            {
                                visible: isCanvasReady,
                                invisible: !isCanvasReady,
                            },
                        )}
                    >
                        <canvas className="h-full w-full" ref={bgCanvasRef} />
                    </div>
                    <div
                        className={clsx(
                            'font-satoshi flex w-full justify-center whitespace-nowrap bg-black px-[38px] pt-[240px] text-[90px] font-[800] text-white sm:text-[260px]',
                            {
                                visible: !isCanvasReady,
                                invisible: isCanvasReady,
                            },
                        )}
                    >
                        Terra-1
                    </div>
                    <div
                        aria-hidden="true"
                        style={{ mixBlendMode: 'darken' }}
                        className={clsx(
                            'font-satoshi pointer-events-none absolute inset-0 flex w-full justify-center whitespace-nowrap bg-black px-[38px] pt-[240px] text-[90px] font-[800] text-white sm:text-[260px]',
                            {
                                visible: isCanvasReady,
                                invisible: !isCanvasReady,
                            },
                        )}
                    >
                        Terra-1
                    </div>
                </div>

                <div
                    className={clsx(
                        'tablet:gap-[16px] tablet:px-0 tablet:pb-[200px] tablet:pt-[80px] z-10 flex w-full flex-col items-center justify-center gap-[13px] bg-black px-[38px] pt-[70px] text-white',
                        {
                            'pb-[113px]': locale === 'en',
                            'pb-[175px]': locale === 'zh',
                        },
                    )}
                >
                    <h1 className="flex flex-col items-center">
                        <div className={clsx('j-h4')}>
                            {t('subtitle.firstLine')}
                        </div>
                        <div className={clsx('j-h4 text-center')}>
                            {t('subtitle.secondLine')}
                        </div>
                    </h1>
                    <div className={clsx('j-t1 w-full text-center')}>
                        <span>{t('more.part1')}</span>
                        <span
                            style={{
                                fontFamily:
                                    'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                                letterSpacing: '0',
                            }}
                        >
                            {locale === 'en' ? '——' : '———'}
                        </span>
                        <span>{t('more.part2')}</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
